import { Box, Slider, Typography } from "@mui/material";

interface Mark {
  value: number;
  label: string;
}

interface SliderWithLabelProps {
  label: string;
  min: number;
  max: number;
  value: number;
  marks?: Mark[];
  step?: number;
  onChange: (value: number) => void;
  padding?: string; // Optional padding prop
}

/**
 * SliderWithLabel — A fully controlled slider component with a label and current value display.
 *
 * Example usage:
 *
 * const [value, setValue] = useState(2048);
 *
 * <SliderWithLabel
 *   label="Context Length"
 *   min={0}
 *   max={4096}
 *   step={256}
 *   value={value}
 *   marks={[
 *     { value: 0, label: "0" },
 *     { value: 2048, label: "2048" },
 *     { value: 4096, label: "4096" },
 *   ]}
 *   onChange={setValue}
 * />
 */
export const SliderWithLabel = ({
  label,
  min,
  max,
  step = 1,
  marks,
  value,
  onChange,
  padding = "4px 8px 8px 8px", // Default padding if not provided
}: SliderWithLabelProps) => {
  const handleChange = (_: Event, newValue: number | number[]) => {
    onChange(Array.isArray(newValue) ? newValue[0] : newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        padding: padding,
      }}
    >
      {/* Header: Label + Current Value */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      </Box>

      {/* Slider */}
      <Slider
        aria-label={label}
        value={value}
        onChange={handleChange}
        step={step}
        min={min}
        max={max}
        marks={marks}
        valueLabelDisplay="auto"
      />
    </Box>
  );
};
